import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { motion } from 'framer-motion';
import ParticleBackground from '../../components/ParticleBackground';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Seo from '../../components/Seo';

export default function SpreadShow({ spread, errors, meta_tags, content_html, other_spreads = [] }) {
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(errors?.question || '');
  const [isThinking, setIsThinking] = useState(false);

  const displayError = error || errors?.question || '';
  const isLoading = isThinking && !errors?.question;
  const isDisabled = isSubmitting && !errors?.question;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!question.trim()) {
      return setError('Please enter a question.');
    }
    if (question.trim().length < 10) {
      return setError('Question is too short. Please be more specific.');
    }

    setIsThinking(true);
    setIsSubmitting(true);

    // Show thinking animation for at least 1.5 seconds
    await new Promise(resolve => setTimeout(resolve, 1500));

    router.post('/readings', {
      reading: {
        question: question,
        spread_slug: spread.slug
      }
    }, {
      onError: () => {
        setIsThinking(false);
        setIsSubmitting(false);
        setError('Something went wrong. Please try again.');
      },
      onSuccess: () => {
        setIsThinking(false);
      }
    });
  };

  const positions = spread.positions || [];

  return (
    <>
      <Seo meta={meta_tags} />

      <div className="bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 min-h-screen relative overflow-hidden flex flex-col">
      <ParticleBackground starCount={30} particleCount={15} />
      <Header />

      <div className="relative z-10 flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section with Question Form */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Animated Icon */}
            <motion.div
              className="inline-block mb-6"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="relative">
                <span className="text-7xl drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]">🎴</span>
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 blur-xl bg-amber-500/30 rounded-full"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6">
              <span className="bg-gradient-to-r from-amber-200 via-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-lg">
                {spread.name}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-purple-100 mb-4 max-w-2xl mx-auto leading-relaxed">
              {spread.description}
            </p>

            <div className="flex items-center justify-center gap-3 text-amber-200">
              <span className="text-2xl">✨</span>
              <p className="text-lg font-semibold">
                {spread.card_count} {spread.card_count === 1 ? 'Card' : 'Cards'} • Free AI Reading
              </p>
              <span className="text-2xl">✨</span>
            </div>
          </motion.div>

          {/* Question Form - PRIMARY CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              {/* Glowing border effect */}
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-30"
                animate={{
                  opacity: [0.2, 0.4, 0.2],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                }}
              />

              <div className="relative bg-gradient-to-br from-purple-900/90 to-indigo-900/90 backdrop-blur-xl p-8 md:p-10 rounded-3xl border-2 border-purple-400/30 shadow-2xl">
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-heading font-bold text-white mb-2 flex items-center justify-center gap-3">
                    <span>🔮</span>
                    Ask Your Question
                    <span>🔮</span>
                  </h2>
                  <p className="text-purple-200">
                    Let the cards reveal your path forward
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Question Input */}
                  <div>
                    <textarea
                      id="question"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      rows={4}
                      className="w-full px-5 py-4 bg-indigo-950/50 border-2 border-purple-400/30 rounded-xl text-white text-lg placeholder-purple-300/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 resize-none transition-all"
                      placeholder="What question weighs on your heart?&#10;&#10;Example: What do I need to know about my future?&#10;Will I find happiness in my career?"
                      required
                      disabled={isDisabled}
                    />
                    <p className="mt-3 text-sm text-purple-200 flex items-center gap-2">
                      <span>💫</span>
                      Be specific and open your heart to receive guidance
                    </p>
                  </div>

                  {/* Error Message */}
                  {displayError && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-4 bg-red-900/30 border-2 border-red-400 rounded-xl text-red-200 text-center"
                    >
                      {displayError}
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isDisabled}
                    className="w-full relative overflow-hidden bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 text-white px-8 py-5 rounded-full text-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    whileHover={!isDisabled ? {
                      scale: 1.02,
                      boxShadow: '0 0 40px rgba(251, 191, 36, 0.8)'
                    } : {}}
                    whileTap={!isDisabled ? { scale: 0.98 } : {}}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-amber-400 via-purple-400 to-pink-400"
                      animate={{
                        x: ['-100%', '100%'],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                      style={{ opacity: 0.3 }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      {isLoading ? (
                        <>
                          <motion.span
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          >
                            🔮
                          </motion.span>
                          Channeling the cosmos...
                        </>
                      ) : (
                        <>
                          ✨ Reveal My Reading ✨
                        </>
                      )}
                    </span>
                  </motion.button>
                </form>

                <p className="text-center text-purple-300/70 text-xs mt-6">
                  🌟 100% Free • No Signup Required • Instant AI Interpretation 🌟
                </p>
              </div>
            </div>
          </motion.div>

          {/* How This Spread Works - Educational Content */}
          {positions.length > 0 && (
            <motion.div
              className="mt-16"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-3">
                  How This Spread Works
                </h2>
                <p className="text-purple-200">
                  Each card position reveals a unique aspect of your question
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {positions.map((position, index) => (
                  <motion.div
                    key={index}
                    className="relative group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  >
                    {/* Card glow on hover */}
                    <motion.div
                      className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-purple-500 rounded-xl opacity-0 group-hover:opacity-50 blur transition-opacity"
                    />

                    <div className="relative flex items-start gap-3 p-5 bg-purple-900/40 backdrop-blur rounded-xl border border-purple-400/20 group-hover:border-amber-400/50 transition-all">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-amber-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                        {index + 1}
                      </div>
                      <div className="flex-1 flex items-center">
                        <h3 className="font-heading font-bold text-amber-200">
                          {typeof position === 'string' ? position : position.name}
                        </h3>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Rich SEO content (server-rendered markdown) */}
          {content_html && (
            <motion.div
              className="mt-16"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur rounded-3xl border border-purple-400/30 p-8 md:p-12">
                <article
                  className="prose prose-invert prose-purple max-w-none
                             prose-headings:font-heading prose-headings:text-amber-200
                             prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-8
                             prose-p:text-purple-100 prose-p:leading-relaxed
                             prose-ul:text-purple-100 prose-li:text-purple-100
                             prose-strong:text-amber-300
                             prose-a:text-amber-300 prose-a:no-underline hover:prose-a:text-amber-200"
                  dangerouslySetInnerHTML={{ __html: content_html }}
                />
              </div>
            </motion.div>
          )}

          {/* Other spreads — internal linking */}
          {other_spreads.length > 0 && (
            <motion.div
              className="mt-16"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-3">
                  Explore Other Free Tarot Readings
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {other_spreads.map((s) => (
                  <a
                    key={s.slug}
                    href={`/readings/${s.slug}`}
                    className="block p-4 bg-purple-900/40 backdrop-blur rounded-xl border border-purple-400/20 hover:border-amber-400/50 transition-all text-center"
                  >
                    <span className="block font-heading font-semibold text-white text-sm">{s.name}</span>
                    <span className="block text-xs text-purple-300 mt-1">
                      {s.card_count} {s.card_count === 1 ? 'card' : 'cards'}
                    </span>
                  </a>
                ))}
              </div>
            </motion.div>
          )}

          {/* Bottom CTA */}
          <motion.div
            className="text-center mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <a
              href="/cards"
              className="inline-flex items-center gap-2 text-purple-300 hover:text-amber-300 transition-colors text-lg group"
            >
              <span className="text-xl">🎴</span>
              Browse all 78 tarot card meanings
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </motion.div>
        </div>
      </div>

      <Footer />
      </div>
    </>
  );
}
